/**
 * Push *policy*: when to prompt, what counts as ready, how long a dismissal lasts.
 *
 * Deliberately free of Firebase and of any browser API, for two reasons. It is
 * the part worth testing, and it is the part the extension shares — importing it
 * must not drag in the window build of `firebase/messaging` that only a portal
 * needs. The mechanics live behind `PushDriver` in `./push-driver`.
 */

/**
 * Push readiness is two independent facts, not one:
 *
 *  - `pushEnabled` is a per-user/per-workspace row in the database
 *  - the FCM token is per **browser profile**
 *
 * Treating the server flag as the whole answer is why a browser with no token
 * could still render "Desktop alerts on", leaving no way to register it. Each
 * combination gets its own status so the UI can offer the action that will
 * actually work.
 */
export type PushSetupStatus =
  | 'loading'
  /** On for the account and registered here. Nothing to prompt. */
  | 'ready'
  /** Never opted in. */
  | 'never-enabled'
  /** Opted in on another browser; this profile has no token yet. */
  | 'device-missing'
  /** Denied at the browser level — not fixable from inside the app. */
  | 'blocked'
  /** Cannot work in this context at all; never prompt for it. */
  | 'unsupported';

/** Everything about push that is only observable on the client. */
export type PushLocalDiagnostics = {
  /** The runtime can do push at all — SDK support, and a worker to host it. */
  supported: boolean;
  vapidKeyConfigured: boolean;
  projectId: string;
  notificationPermission: string;
  /** The browser-level push subscription. Absent means getToken never completed. */
  pushSubscribed: boolean;
  /** A token was obtained and handed to the backend from this profile. */
  tokenStored: boolean;
  /** Last few characters only — enough to tell two profiles apart in a bug report. */
  tokenTail: string | null;
};

export type DismissRecord = Partial<Record<PushSetupStatus, number>>;

/** Prompts return after this long, so "Not now" is respected without being final. */
export const DISMISS_TTL_MS = 14 * 24 * 60 * 60 * 1000;

/**
 * Order matters: the states nothing can be done about are settled first, because
 * surfacing one as a prompt offers the user a button that cannot work.
 */
export function derivePushSetupStatus(
  diagnostics: PushLocalDiagnostics | null,
  pushEnabled: boolean | undefined
): PushSetupStatus {
  if (!diagnostics || pushEnabled === undefined) return 'loading';

  if (!diagnostics.supported) return 'unsupported';
  if (!diagnostics.vapidKeyConfigured) return 'unsupported';
  if (diagnostics.notificationPermission === 'denied') return 'blocked';

  if (!pushEnabled) return 'never-enabled';
  return diagnostics.tokenStored && diagnostics.pushSubscribed ? 'ready' : 'device-missing';
}

/** Only these three have an action the user can usefully take from inside the app. */
export function shouldPromptForPush(status: PushSetupStatus, dismissals: DismissRecord | null) {
  if (dismissals === null) return false;
  if (status !== 'never-enabled' && status !== 'device-missing' && status !== 'blocked') {
    return false;
  }
  // Keyed per status: dismissing "not set up" must not also hide the very
  // different "this browser isn't registered" message.
  const dismissedAt = dismissals[status];
  return !dismissedAt || Date.now() - dismissedAt >= DISMISS_TTL_MS;
}

/** The mechanics each surface supplies for the policy above to drive. */
export interface PushDriver {
  /** Permission → token → backend registration. Throws with a user-facing reason. */
  register(): Promise<string | null>;
  unregister(): Promise<void>;
  diagnostics(): Promise<PushLocalDiagnostics | null>;
  readDismissals(): Promise<DismissRecord>;
  writeDismissals(record: DismissRecord): Promise<void>;
}
